/**
 * Pixel preparation for the software digital-zoom decode pass.
 *
 * ZXing works on a single grey channel, so the RGBA the canvas hands back is
 * reduced once here instead of inside the decoder. Both helpers write into a
 * caller-owned buffer: the scan loop runs several times a second and must not
 * allocate a megabyte-sized array per frame.
 */

/**
 * BT.601 luma with integer weights — the same approximation ZXing uses for its
 * own canvas source, so the decoder sees the greys it was tuned on.
 * Alpha is ignored: a frame drawn from a video element is always opaque.
 */
export function toLuminance(
    rgba: Uint8ClampedArray,
    out: Uint8ClampedArray,
): Uint8ClampedArray {
    const pixels = rgba.length >> 2
    if (out.length < pixels) {
        throw new RangeError(
            `luminance buffer holds ${out.length} of ${pixels} pixels`,
        )
    }
    for (let i = 0; i < pixels; i++) {
        const p = i << 2
        // + 512 rounds the fixed-point division instead of truncating it.
        out[i] =
            (306 * rgba[p] + 601 * rgba[p + 1] + 117 * rgba[p + 2] + 512) >> 10
    }
    return out
}

/**
 * 3x3 sharpen (centre 5, four neighbours -1). Upscaling a small crop softens
 * the bar edges; this pulls them back apart so the binarizer sees a step rather
 * than a ramp. The one-pixel border is copied unchanged — the kernel has no
 * neighbours there, and a barcode never sits on the very edge of the aiming
 * frame. Frames too small for the kernel are copied through.
 */
export function sharpenLuminance(
    src: Uint8ClampedArray,
    out: Uint8ClampedArray,
    width: number,
    height: number,
): Uint8ClampedArray {
    const pixels = width * height
    if (src.length < pixels || out.length < pixels) {
        throw new RangeError(
            `sharpen buffers hold ${src.length}/${out.length} of ${pixels} pixels`,
        )
    }
    out.set(src.subarray(0, pixels))
    if (width < 3 || height < 3) return out

    for (let y = 1; y < height - 1; y++) {
        const row = y * width
        for (let x = 1; x < width - 1; x++) {
            const i = row + x
            const value =
                5 * src[i] -
                src[i - 1] -
                src[i + 1] -
                src[i - width] -
                src[i + width]
            // Uint8ClampedArray clamps to 0..255 on assignment.
            out[i] = value
        }
    }
    return out
}
